import { AsyncHelper } from '@bryntum/schedulerpro';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { User } from 'api/models';
import { ResourceManagmentApi } from 'api/resource-management';
import { ResourceType } from 'api/resource-management/types';
import { toastService } from 'components/molecules/toast';
import {
  AvailabilityFilter,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import { ApiGroupFilter } from 'utils/simple-search-filter/simple-search-filter';
import { SchedulerResourceType } from '../resource-types';
import { useLoadingRenderer } from '../scheduler-config/loading-renderer';
import { useNoDataRender } from '../scheduler-config/no-data-renderer';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { getUsersSorter } from '../sorters';
import {
  getTimeRangesForAvailabilityFilter,
  idsForTimeRanges,
} from '../utils/availability-filter-range';
import {
  resourceIdForProjectAsLeaf,
  resourceIdForTeamAsLeaf,
} from '../utils/id';
import {
  checkResourceAvailability,
  isMatchingResource,
} from '../utils/resource-matching';

type FilteredResources = Record<number, string[]>;

interface Props {
  users: User[];
  canApplyFilter: boolean;
  hideUnavailableAreas: boolean;
  searchText: string;
  filter: GroupedFilter;
  schedulerRef: SchedulerReference;
  currentViewPreset: ViewPresetId;
  availableResources?: number[];
  availabilityFilter?: AvailabilityFilter;
  onFilterReset: () => void;
}
export const usePeopleViewFilter = ({
  canApplyFilter,
  hideUnavailableAreas,
  users,
  searchText,
  filter,
  schedulerRef,
  currentViewPreset,
  availabilityFilter,
  availableResources,
  onFilterReset,
}: Props): void => {
  const { showLoading, hideLoading } = useLoadingRenderer({ schedulerRef });

  const translate = useTranslate();
  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');

  const [noData, setNoData] = useState(false);
  const [previousFilter, setPreviousFilter] =
    useState<GroupedFilter | undefined>(undefined);
  const [filteredUserResources, setFilteredUserResources] =
    useState<FilteredResources | undefined>(undefined);
  const [filteredRoleResources, setFilteredRoleResources] =
    useState<FilteredResources | undefined>(undefined);

  useNoDataRender({
    schedulerRef,
    noData,
    onReset: onFilterReset,
  });

  const filterApiHandler = useCallback(async () => {
    try {
      showLoading();
      await AsyncHelper.sleep(10);

      const { peopleTabEntities } =
        await ResourceManagmentApi.getFilteredResourcesInPeople(filter);

      const userResources = {} as FilteredResources;
      const roleResources = {} as FilteredResources;

      peopleTabEntities.forEach((resource) => {
        if (resource.type === ResourceType.User) {
          userResources[resource.userId] = (
            userResources[resource.userId] ?? []
          ).concat(
            ...resource.projects.map(({ projectId, referenceTeamId, roleId }) =>
              resourceIdForProjectAsLeaf({
                projectId,
                referenceTeamId,
                roleId,
                userId: resource.userId,
              }),
            ),
          );
        } else {
          roleResources[resource.roleId] = (
            roleResources[resource.roleId] ?? []
          ).concat(
            ...resource.projects.map(({ projectId, teamId }) =>
              resourceIdForTeamAsLeaf(projectId, teamId),
            ),
          );
        }
      });

      hideLoading();

      setFilteredUserResources(userResources);
      setFilteredRoleResources(roleResources);
    } catch (e) {
      toastService({
        title: errorTitle,
        message: errorMessage,
      }).error();

      hideLoading();
      setFilteredUserResources(undefined);
      setFilteredRoleResources(undefined);
    }
  }, [errorMessage, errorTitle, filter, hideLoading, showLoading]);

  const filterApiRef = useRef(filterApiHandler);

  useEffect(() => {
    filterApiRef.current = filterApiHandler;
  }, [filterApiHandler]);

  const debouncedFilterApi = useMemo(
    () =>
      debounce(() => {
        filterApiRef.current();
      }, 200),
    [],
  );

  const searchFunction = useMemo(
    () => isMatchingResource(searchText),
    [searchText],
  );

  useEffect(() => {
    const isFilterValid =
      filter.groupCriteria.filter((x) => FieldFilter.isValid(x.criteria))
        .length === filter.groupCriteria.length;

    const oldFilterQuery =
      previousFilter != null
        ? ApiGroupFilter.toQueryStringFormatForGroups(
            previousFilter.groupCriteria,
          )
        : undefined;
    const newFilterQuery = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.groupCriteria,
    );

    if (
      isFilterValid &&
      (previousFilter == null ||
        newFilterQuery !== oldFilterQuery ||
        previousFilter.type !== filter.type)
    ) {
      setPreviousFilter(filter);
      if (newFilterQuery === '') {
        setFilteredUserResources(undefined);
        setFilteredRoleResources(undefined);
      } else {
        debouncedFilterApi();
      }
    }
  }, [debouncedFilterApi, filter, previousFilter]);
  const searchResource = useCallback(
    (resource) => {
      const type = resource.get('type');

      switch (type) {
        case SchedulerResourceType.AssignProject:
          return (
            searchFunction(resource.parent) ||
            (resource.parent?.parent != null &&
              searchFunction(resource.parent.parent)) ||
            !!resource.parent.children.find(
              (child) =>
                child.type === SchedulerResourceType.Project &&
                searchFunction(child),
            )
          );
        case SchedulerResourceType.Project:
          return (
            searchFunction(resource) ||
            searchFunction(resource.parent) ||
            (resource.parent?.parent != null &&
              searchFunction(resource.parent.parent))
          );
        case SchedulerResourceType.User:
          return (
            (resource.parent != null && searchFunction(resource.parent)) ||
            searchFunction(resource)
          );
        case SchedulerResourceType.Team:
          return (
            searchFunction(resource) ||
            (resource.parent != null && searchFunction(resource.parent)) ||
            (resource.parent?.parent != null &&
              searchFunction(resource.parent.parent))
          );
        default:
          return searchFunction(resource);
      }
    },
    [searchFunction],
  );

  const applyFilter = useCallback(() => {
    try {
      schedulerRef.current.instance.resourceStore.filter({
        filters: {
          id: 'people-view-filter',
          filterBy: (resource) => {
            const type = resource.get('type');
            const searchResult = searchResource(resource);
            const availabilityFilterResult = checkResourceAvailability(
              [
                SchedulerResourceType.Project,
                SchedulerResourceType.AssignProject,
              ].includes(type)
                ? resource.parent
                : resource,
              availabilityFilter,
              availableResources,
            );

            let filterResult = false;

            switch (type) {
              case SchedulerResourceType.ExpandableRole:
                queueMacrotask(() => {
                  resource.set('teamCount', resource.children.length);
                });

                filterResult =
                  filteredRoleResources == null ||
                  filteredRoleResources[resource.get('roleId')] != null;
                break;
              case SchedulerResourceType.Team:
                filterResult =
                  filteredRoleResources == null ||
                  (filteredRoleResources[resource.parent.get('roleId')] !=
                    null &&
                    filteredRoleResources[
                      resource.parent.get('roleId')
                    ].includes(
                      resourceIdForTeamAsLeaf(
                        resource.get('projectId'),
                        resource.get('teamId'),
                      ),
                    ));
                break;

              case SchedulerResourceType.User:
                filterResult =
                  filteredUserResources == null ||
                  filteredUserResources[resource.get('userId')] != null;
                break;
              case SchedulerResourceType.Project:
                filterResult =
                  filteredUserResources == null ||
                  (filteredUserResources[resource.parent.get('userId')] !=
                    null &&
                    filteredUserResources[
                      resource.parent.get('userId')
                    ].includes(
                      resourceIdForProjectAsLeaf({
                        projectId: resource.get('projectId'),
                        referenceTeamId: resource.get('referenceTeam')?.teamId,
                        roleId: resource.get('roleId'),
                        userId: resource.get('userId'),
                      }),
                    ));
                break;
              case SchedulerResourceType.AssignProject:
                filterResult =
                  filteredUserResources == null ||
                  filteredUserResources[resource.parent.get('userId')] != null;
                break;
              default:
                return filterResult;
            }

            return searchResult && filterResult && availabilityFilterResult;
          },
        },
        reapplyFilterOnAdd: true,
        reapplyFilterOnUpdate: true,
      });
      schedulerRef.current.instance.resourceStore.sort(getUsersSorter());
      setNoData(schedulerRef.current?.instance.resourceStore.allCount === 0);
    } catch (e) {
      logger.log('Error when filtering in people view', e);
    }
  }, [
    availabilityFilter,
    availableResources,
    filteredRoleResources,
    filteredUserResources,
    schedulerRef,
    searchResource,
  ]);

  const filterRef = useRef(applyFilter);

  useEffect(() => {
    filterRef.current = applyFilter;
  }, [applyFilter]);

  const debouncedFilter = useMemo(
    () =>
      debounce(() => {
        filterRef.current();
      }, 200),
    [],
  );

  useEffect(() => {
    if (!canApplyFilter) {
      return;
    }

    debouncedFilter();
  }, [canApplyFilter, debouncedFilter, applyFilter]);

  const removeTimeRangesForAvailabilityFilter = useCallback(() => {
    idsForTimeRanges.forEach((id) => {
      const range =
        schedulerRef.current.instance.project.timeRangeStore.getById(id);

      if (range) {
        schedulerRef.current.instance.project.timeRangeStore.remove(range);
      }
    });
  }, [schedulerRef]);

  useEffect(() => {
    if (availabilityFilter && canApplyFilter) {
      queueMacrotask(() => {
        schedulerRef.current.instance.scrollToDate(
          availabilityFilter.startDate
            .subtract(
              1,
              [ViewPresetId.Day, ViewPresetId.DayZoomed].includes(
                currentViewPreset,
              )
                ? 'day'
                : 'week',
            )
            .toDate(),
          {
            block: 'start',
          },
        );
      });
    }
    // eslint-disable-next-line no-param-reassign
    schedulerRef.current.instance.availabilityFilter = availabilityFilter;
  }, [
    availabilityFilter,
    canApplyFilter,
    currentViewPreset,
    schedulerRef,
    translate,
    users,
  ]);

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    schedulerRef.current.instance.cls = {
      'availability-filter-applied':
        !!availabilityFilter && hideUnavailableAreas,
    };

    if (availabilityFilter && canApplyFilter) {
      schedulerRef.current.instance.suspendRefresh();
      removeTimeRangesForAvailabilityFilter();
      schedulerRef.current.instance.project.timeRangeStore.add(
        getTimeRangesForAvailabilityFilter(
          availabilityFilter,
          hideUnavailableAreas,
          translate,
        ),
      );
      schedulerRef.current.instance.resumeRefresh(true);
    } else {
      removeTimeRangesForAvailabilityFilter();
    }
  }, [
    availabilityFilter,
    canApplyFilter,
    hideUnavailableAreas,
    removeTimeRangesForAvailabilityFilter,
    schedulerRef,
    translate,
  ]);
};
